import { PlatformType } from "../helpers/subdomainHelpers/getPlatformType";
import { MarketplaceCurrentOffer, MarketplaceSessionEnrichment, MarketplaceSource } from "./types";

export const MARKETPLACE_SESSION_KEY = "gp.marketplaceAnalytics.session";

export type MarketplaceSessionState = {
  source: MarketplaceSource | null;
  source_detail: string | null;
  platform: PlatformType | null;
  current_offer: MarketplaceCurrentOffer | null;
  /** Internal only — true when the user left an unbooked offer for a different one. Not sent on dataLayer. */
  offer_abandoned: boolean;
  /** Internal only — whether the current offer has received a book click. */
  current_offer_booked: boolean;
};

/** In-memory fallback when sessionStorage is unavailable. */
let memorySession: MarketplaceSessionState | null = null;

const normalize = (value?: string | null): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = String(value).trim();
  return trimmed || null;
};

const readFromStorage = (): MarketplaceSessionState | null => {
  try {
    const raw = sessionStorage.getItem(MARKETPLACE_SESSION_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as MarketplaceSessionState;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const writeToStorage = (state: MarketplaceSessionState): void => {
  memorySession = state;
  try {
    sessionStorage.setItem(MARKETPLACE_SESSION_KEY, JSON.stringify(state));
  } catch {
    // Private mode / blocked storage — memory fallback only.
  }
};

export const readMarketplaceSession = (): MarketplaceSessionState | null => {
  return readFromStorage() ?? memorySession;
};

/** @internal test helper — clears storage + memory. */
export const resetMarketplaceSessionForTests = (): void => {
  memorySession = null;
  try {
    sessionStorage.removeItem(MARKETPLACE_SESSION_KEY);
  } catch {
    // ignore
  }
};

/**
 * Maps raw inbound params to stable wiki §6a source values.
 * Prefers `source`, then `utm_source`. Detail from `utm_campaign` / `utm_content` / leftover raw token.
 */
export const captureMarketplaceSourceFromSearch = (
  search: string
): { source: MarketplaceSource | null; source_detail: string | null } => {
  try {
    const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
    const raw = normalize(params.get("source")) ?? normalize(params.get("utm_source"));
    const detailFromParams =
      normalize(params.get("utm_campaign")) ??
      normalize(params.get("utm_content")) ??
      normalize(params.get("source_detail"));

    if (!raw) {
      return { source: null, source_detail: detailFromParams };
    }

    const lower = raw.toLowerCase();

    if (lower === "newsletter" || lower.includes("newsletter")) {
      return { source: "newsletter", source_detail: detailFromParams };
    }

    if (
      lower === "mmb" ||
      lower === "booking" ||
      lower.includes("mmb") ||
      lower.includes("manage_my_booking") ||
      lower.includes("manage-my-booking") ||
      lower.includes("managemybooking")
    ) {
      return {
        source: "mmb",
        source_detail: detailFromParams ?? (lower !== "mmb" && lower !== "booking" ? lower : null)
      };
    }

    if (lower === "direct") {
      return { source: "direct", source_detail: detailFromParams };
    }

    return { source: "other", source_detail: detailFromParams ?? lower };
  } catch {
    return { source: null, source_detail: null };
  }
};

const createInitialSession = (
  platform: PlatformType,
  sourceFields: { source: MarketplaceSource | null; source_detail: string | null }
): MarketplaceSessionState => ({
  source: sourceFields.source,
  source_detail: sourceFields.source_detail,
  platform,
  current_offer: null,
  offer_abandoned: false,
  current_offer_booked: false
});

/**
 * Ensures a session exists. Captures source only on first init.
 * Subsequent calls keep the existing source fields.
 */
export const initMarketplaceSession = (
  platform: PlatformType,
  search: string = typeof window !== "undefined" ? window.location.search : ""
): MarketplaceSessionState => {
  const existing = readMarketplaceSession();
  if (existing) {
    if (existing.platform !== platform) {
      const updated = { ...existing, platform };
      writeToStorage(updated);
      return updated;
    }

    return existing;
  }

  const state = createInitialSession(platform, captureMarketplaceSourceFromSearch(search));
  writeToStorage(state);
  return state;
};

/**
 * Same offer identity when:
 * - both have offer_code and codes match, or
 * - same offer_type and at least one side is missing offer_code (hydrate / type-only identity).
 */
export const isSameOfferIdentity = (a: MarketplaceCurrentOffer | null, b: MarketplaceCurrentOffer | null): boolean => {
  if (!a || !b) {
    return false;
  }

  const aCode = normalize(a.offer_code);
  const bCode = normalize(b.offer_code);
  const aType = normalize(a.offer_type);
  const bType = normalize(b.offer_type);

  if (aCode && bCode) {
    return aCode === bCode;
  }

  if (aType && bType && aType === bType) {
    return true;
  }

  return false;
};

const mergeOffer = (prev: MarketplaceCurrentOffer | null, next: MarketplaceCurrentOffer): MarketplaceCurrentOffer => ({
  offer_code: normalize(next.offer_code) ?? normalize(prev?.offer_code) ?? null,
  offer_type: normalize(next.offer_type) ?? normalize(prev?.offer_type) ?? null
});

/**
 * Updates current offer. Returns whether a `select_offer_transfer_hub` dataLayer push should fire.
 * - First offer or different offer → shouldPush true
 * - Same identity (incl. offer_code hydrate on same type) → shouldPush false
 */
export const applySelectOffer = (
  nextOffer: MarketplaceCurrentOffer,
  platform?: PlatformType
): { shouldPush: boolean; state: MarketplaceSessionState } => {
  const session = platform
    ? initMarketplaceSession(platform)
    : readMarketplaceSession() ?? initMarketplaceSession("hotel");

  const normalizedNext: MarketplaceCurrentOffer = {
    offer_code: normalize(nextOffer.offer_code),
    offer_type: normalize(nextOffer.offer_type)
  };

  if (isSameOfferIdentity(session.current_offer, normalizedNext)) {
    const updated: MarketplaceSessionState = {
      ...session,
      current_offer: mergeOffer(session.current_offer, normalizedNext)
    };
    writeToStorage(updated);
    return { shouldPush: false, state: updated };
  }

  const leftUnbookedOffer = Boolean(session.current_offer) && !session.current_offer_booked;
  const updated: MarketplaceSessionState = {
    ...session,
    current_offer: normalizedNext,
    offer_abandoned: leftUnbookedOffer ? true : session.offer_abandoned,
    current_offer_booked: false
  };
  writeToStorage(updated);
  return { shouldPush: true, state: updated };
};

/** Marks the current offer as booked (call before / with book snapshot). */
export const markCurrentOfferBooked = (): MarketplaceSessionState | null => {
  const session = readMarketplaceSession();
  if (!session) {
    return null;
  }

  const updated: MarketplaceSessionState = {
    ...session,
    current_offer_booked: true
  };
  writeToStorage(updated);
  return updated;
};

/** Session fields to merge into a marketplace_context payload (wiki-aligned). */
export const getMarketplaceSessionPayloadFields = (): MarketplaceSessionEnrichment => {
  const session = readMarketplaceSession();

  return {
    source: session?.source ?? null,
    source_detail: session?.source_detail ?? null,
    type: session?.current_offer?.offer_type ?? null
  };
};
